import {
  Typography,
  Container,
  Grid,
  styled,
  Paper,
  useTheme,
  useMediaQuery,
  Box,
  Button,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Table,
  TableBody,
  CircularProgress,
} from '@mui/material';
import Page from '../components/Page';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import UncheckedIcon from '../assets/icons/uncheckbox-icon';
import CheckboxIcon from '../assets/icons/checkbox-icon';
import Checkbox from '@mui/material/Checkbox';
import PlanModal from '../components/PlanModal';
import config from '../config';
import { Cuota } from '../features/cuentacorriente/ctaCorrienteAPI';
import { CreatedPlans, VistaPreviaPlanes } from '../features/planes/planesAPI';
import { ImponibleData } from '../features/imponibles/imponiblesAPI';
import { store } from '../store';
import { cleanPlanState, getPlanPreview, planStatus, registerPlan } from '../features/planes/planesSlice';
import { useSelector } from 'react-redux';

const PaymentsWrapper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '1rem 2rem 1rem 1rem',
}));

const CustomButton = styled(Button)(() => ({
  color: 'white',
  padding: '.4rem 2rem',
  fontSize: '.75rem',
  fontWeight: '700',
}));

const TableTitle = styled(Typography)(({ theme }) => ({
  fontWeight: '500',
  fontSize: '.85rem',
  color: theme.palette.grey[400],
}));

const TableBold = styled(Typography)(({ theme }) => ({
  fontWeight: '700',
  fontSize: '.85rem',
  color: theme.palette.grey[500],
}));

const TableRegular = styled(Typography)(({ theme }) => ({
  fontWeight: '500',
  fontSize: '.85rem',
  color: theme.palette.grey[500],
}));

interface PlanProposalState {
  selectedCuotas: Cuota[];
  selectedPlan: CreatedPlans;
  resourceNumber: string;
  imponible: ImponibleData;
  total: number;
}

export default function PlanProposal() {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { state } = useLocation();

  const { selectedCuotas, selectedPlan, resourceNumber, imponible } = state as PlanProposalState;

  const status = useSelector(planStatus);

  const [choosedPlan, setChoosedPlan] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [informacionCuotas, setInformacionCuotas] = useState<VistaPreviaPlanes>();

  const [cuotasPendientes, setCuotasPendientes] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    store.dispatch(getPlanPreview({
      tipo: imponible.tipo,
      id: imponible.imponible,
      recurso: resourceNumber,
      propuesta: selectedPlan.id,
      cuotas: Array.from({ length: selectedPlan.limite_cant_cuotas - selectedPlan.minimo_cant_cuotas + 1 })
        .map((_, i) => selectedPlan.minimo_cant_cuotas + i),
    }))
      .unwrap()
      .then(planes => {
        setInformacionCuotas(planes);
      })
      .finally(() => setIsLoading(false));

    const newCuotasPendientes = selectedCuotas.some(cuota => {
      const año = parseInt(cuota.anio);
      return año < selectedPlan.anio_ini_incl || año > selectedPlan.anio_fin_incl;
    });

    setCuotasPendientes(newCuotasPendientes);
  }, []);

  const handlePlanConfirmation = () => {
    try {
      setIsLoading(true);
      store.dispatch(registerPlan({
        cuotas: choosedPlan.toString(),
        recurso: resourceNumber,
        tipo: imponible.tipo,
        id: imponible.imponible,
        planId: selectedPlan.id,
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanFinalization = () => {
    store.dispatch(cleanPlanState());
    setOpenModal(false);

    if (status !== 'success') {
      setIsLoading(false);
      return;
    }

    if (!cuotasPendientes) {
      navigate('/');
      return;
    }

    const newSelectedCuotas = selectedCuotas.filter(cuota => {
      const año = parseInt(cuota.anio);
      return año < selectedPlan.anio_ini_incl || año > selectedPlan.anio_fin_incl;
    });

    navigate('/planes', {
      state: {
        selectedCuotas: newSelectedCuotas,
        resourceNumber,
        imponible,
      },
    });
  };

  const handleGoBack = () => {
    navigate('/planes', {
      state: {
        selectedCuotas,
        resourceNumber,
        imponible,
      },
    });
  };

  return (
    <Page>
      <Container>
        <Grid
          container
          padding={'1.875rem'}
          justifyContent="center"
          direction={desktop ? 'row' : 'column'}
        >
          <Grid item sm={8} sx={{ alignSelf: 'center' }}>
            <PaymentsWrapper>
              <Typography variant="subtitle1" mb="1rem">
                Propuesta plan de pago
              </Typography>
              <Typography variant="body1" mb="1rem">
                Los valores informados son informativos y no representan el valor final de las cuotas. Pueden aplicar
                descuentos o recargos. Para obtener una propuesta final de un plan seleccione la cantidad de cuotas y
                haga click en &quot;Imprimir Propuesta&quot;
              </Typography>

              {
                selectedCuotas
                  ? <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 650 }} size="small" aria-label="simple table">
                        <TableHead sx={{ height: '2.5rem' }}>
                          <TableRow>
                            <TableCell align="center">
                              <TableTitle></TableTitle>
                            </TableCell>
                            <TableCell align="center">
                              <TableTitle>Cant. Cuotas</TableTitle>
                            </TableCell>
                            <TableCell align="center">
                              <TableTitle>Interés Financiación</TableTitle>
                            </TableCell>
                            <TableCell align="center">
                              <TableTitle>Valor Cuota</TableTitle>
                            </TableCell>
                            <TableCell align="center">
                              <TableTitle>Total Financiado</TableTitle>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            informacionCuotas
                              ? Object.keys(informacionCuotas).map(cuota => (
                                <TableRow
                                  key={ `plan_${ cuota }` }
                                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                  <TableCell align="center">
                                    <Checkbox
                                      checked={ parseInt(cuota) === choosedPlan ? true : false }
                                      onClick={ () => setChoosedPlan(parseInt(cuota)) }
                                      icon={<UncheckedIcon />}
                                      checkedIcon={<CheckboxIcon />}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <TableRegular>{ parseInt(cuota) }</TableRegular>
                                  </TableCell>
                                  <TableCell align="center">{ selectedPlan.int_fin }%</TableCell>
                                  <TableCell align="center">
                                    <TableRegular>$ {
                                      Math.round(parseFloat(informacionCuotas[cuota][0].monto) * 100) / 100
                                    }</TableRegular>
                                  </TableCell>
                                  <TableCell align="center">
                                    <TableBold>
                                      $ {
                                        Math.round(informacionCuotas[cuota]
                                          .reduce((ax, c) => ax + parseFloat(c.monto), 0) * 100) / 100
                                      }
                                    </TableBold>
                                  </TableCell>
                                  <TableCell align="center"></TableCell>
                                </TableRow>
                              ))
                              : <>
                                  <TableRow>
                                    <TableCell sx={{ textAlign: 'center' }}><CircularProgress size={20} /></TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}><CircularProgress size={20} /></TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}><CircularProgress size={20} /></TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}><CircularProgress size={20} /></TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}><CircularProgress size={20} /></TableCell>
                                  </TableRow>
                                </>
                          }
                        </TableBody>
                      </Table>
                    </TableContainer>
                  : <CircularProgress size={20} />
              }

              <Box my={3} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <CustomButton onClick={ handleGoBack } variant="contained">
                  Volver
                </CustomButton>
                <Box>
                  <CustomButton
                    sx={{ marginRight: '.5rem' }}
                    disabled={choosedPlan === 0 ? true : false}
                    onClick={() => {
                      const comprobanteURL = `${
                        config.api.url
                      }/pdf/propuesta/${
                        imponible.tipo
                      }-${imponible.imponible}/${resourceNumber}?propuesta=${selectedPlan.id}&cuotas=${choosedPlan}`;
                      window.open(comprobanteURL);
                    }}
                    variant="contained"
                  >
                    Imprimir propuesta
                  </CustomButton>
                  <CustomButton
                    disabled={ choosedPlan === 0 }
                    onClick={ () => setOpenModal(true) }
                    variant="contained"
                  >
                    Confirmar plan de pago
                  </CustomButton>
                </Box>
              </Box>
              <PlanModal
                openModal={ openModal }
                setOpenModal={ setOpenModal }
                cuotas={ choosedPlan }
                handlePlanConfirmation={ handlePlanConfirmation }
                handlePlanFinalization={ handlePlanFinalization }
                isLoading={ isLoading }
              />
            </PaymentsWrapper>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
